import React, { useState, useMemo, useEffect } from 'react';
import { Download, Loader2 } from 'lucide-react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { Button, PageHeader, Tabs, Snackbar } from '../../components/ui';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { PLFilters, usePLFilters } from '../../features/p-and-l';
import { getPLTabItems } from '../../features/p-and-l/config/tabs';
import {
	getDateRangeFromMonthYear,
	useExpenditureData,
	useUnitEconomicsData,
	useEBITDAData,
	useEscalationData,
} from '../../features/p-and-l/hooks/usePLTabData';
import { exportPLToExcel } from '../../features/p-and-l/utils/exportPLToExcel';
import { canFilterByCity, requiresCitySelection } from '../../utils/cityFilterPermissions';

/**
 * P&L Summary Page
 * Displays profit and loss summary data with tabbed interface
 */
export const PLSummary: React.FC = () => {
	const { user } = useSelector((state: RootState) => state.auth);
	const {
		selectedMonth,
		selectedYear,
		selectedCity,
		selectedFacility,
		monthOptions,
		yearOptions,
		cityOptions,
		facilityOptions,
		facilities,
		setSelectedMonth,
		setSelectedYear,
		setSelectedCity,
		setSelectedFacility,
	} = usePLFilters();

	const [activeTab, setActiveTab] = useState<string>('ebitda');
	const [downloading, setDownloading] = useState(false);
	const [snackbar, setSnackbar] = useState({
		open: false,
		message: '',
		type: 'error' as 'success' | 'error' | 'info',
	});

	// Determine if we should fetch - all filters must be set.
	// Only role 4 must also have an explicit city selected; founders (31) can
	// fetch an all-city view as soon as a facility is picked — see #56.
	const shouldFetch = !!(
		selectedMonth &&
		selectedYear &&
		selectedFacility &&
		(!requiresCitySelection(user?.userTypeId) || selectedCity)
	);

	// Determine city_id to use:
	//   - not a city-filter role -> the user's own assigned city.
	//   - city-filter role with a city picked -> that city.
	//   - city-filter role with no city picked (founder default) -> fall back
	//     to the selected facility's own city, since every facility belongs
	//     to exactly one (#56 — a founder shouldn't have to pick both).
	const selectedFacilityCityId = facilities.find(f => f.id.toString() === selectedFacility)?.city_id;
	const cityId = canFilterByCity(user?.userTypeId)
		? (selectedCity ? parseInt(selectedCity) : selectedFacilityCityId)
		: user?.city_id;

	// Call APIs at page level - regardless of active tab
	// This ensures all APIs are called when filters change, not when tabs change
	// Note: ClientWisePLData is called inside the ClientWisePLTab component with its own week filter
	const expenditureQuery = useExpenditureData(
		selectedFacility,
		selectedMonth,
		selectedYear,
		shouldFetch
	);
	const unitEconomicsQuery = useUnitEconomicsData(
		cityId,
		selectedFacility,
		selectedMonth,
		selectedYear,
		shouldFetch
	);
	const ebitdaQuery = useEBITDAData(
		cityId,
		selectedFacility,
		selectedMonth,
		selectedYear,
		shouldFetch
	);
	const escalationsQuery = useEscalationData(
		cityId,
		selectedFacility,
		selectedMonth,
		selectedYear,
		shouldFetch
	);

	// Handle errors from any API
	// Note: ClientWisePL errors are handled in the tab component via onError callback
	useEffect(() => {
		const errors = [
			expenditureQuery.error,
			unitEconomicsQuery.error,
			ebitdaQuery.error,
			escalationsQuery.error,
		].filter(Boolean);

		if (errors.length > 0 && shouldFetch) {
			const firstError = errors[0];
			if (firstError) {
				setSnackbar({
					open: true,
					message: `Failed to load data: ${firstError.message}`,
					type: 'error',
				});
			}
		}
	}, [
		expenditureQuery.error,
		unitEconomicsQuery.error,
		ebitdaQuery.error,
		escalationsQuery.error,
		shouldFetch,
		cityId,
	]);

	// Handle search - no-op since APIs auto-fetch on filter change
	const handleSearch = () => {
		// APIs are already called automatically when filters change
		// This is kept for UI consistency
	};

	// Download the ENTIRE P&L review (all 5 tabs) as a multi-sheet Excel.
	const handleDownload = async () => {
		if (!shouldFetch || !cityId || downloading) return;
		setDownloading(true);
		try {
			const { start_date, end_date } = getDateRangeFromMonthYear(selectedMonth, selectedYear);
			const monthLabel = monthOptions.find(o => o.value === selectedMonth)?.label || selectedMonth;
			const facilityLabel =
				facilityOptions.find(o => o.value === selectedFacility)?.label || `facility-${selectedFacility}`;
			const filename = await exportPLToExcel({
				cityId,
				facilityId: parseInt(selectedFacility, 10),
				facilityName: facilityLabel,
				month: monthLabel,
				year: selectedYear,
				startDate: start_date,
				endDate: end_date,
			});
			setSnackbar({ open: true, message: `Downloaded ${filename}`, type: 'success' });
		} catch (err) {
			setSnackbar({
				open: true,
				message: `Could not generate the P&L export: ${(err as Error)?.message || 'unknown error'}`,
				type: 'error',
			});
		} finally {
			setDownloading(false);
		}
	};

	// Get tab items from config - pass data directly instead of enabled flag
	const tabItems = useMemo(
		() =>
			getPLTabItems({
				cityId,
				facilityId: selectedFacility,
				month: selectedMonth,
				year: selectedYear,
				enabled: true, // Always enabled since we're calling APIs at page level
				onError: (message: string) => {
					setSnackbar({
						open: true,
						message,
						type: 'error',
					});
				},
			}),
		[cityId, selectedFacility, selectedMonth, selectedYear]
	);

	return (
		<ErrorBoundary>
			<div className='space-y-6'>
				<div className='flex items-start justify-between gap-4'>
					<PageHeader
						title='P&L Summary'
						locationName={user?.city_name || 'City'}
						totalItems={0}
						itemType=''
						icon='💰'
					/>
					<Button
						onClick={handleDownload}
						disabled={!shouldFetch || downloading}
						title={!shouldFetch ? 'Select month, year and facility first' : 'Download the entire P&L review as Excel'}
						className='inline-flex shrink-0 items-center gap-2'
					>
						{downloading ? <Loader2 className='h-4 w-4 animate-spin' /> : <Download className='h-4 w-4' />}
						{downloading ? 'Preparing…' : 'Download P&L'}
					</Button>
				</div>

				<PLFilters
					selectedMonth={selectedMonth}
					selectedYear={selectedYear}
					selectedCity={selectedCity}
					selectedFacility={selectedFacility}
					monthOptions={monthOptions}
					yearOptions={yearOptions}
					cityOptions={cityOptions}
					facilityOptions={facilityOptions}
					onMonthChange={setSelectedMonth}
					onYearChange={setSelectedYear}
					onCityChange={setSelectedCity}
					onFacilityChange={setSelectedFacility}
					onSearch={handleSearch}
				/>

				<ErrorBoundary>
					<div className='bg-white rounded-lg shadow-sm'>
						<Tabs
							items={tabItems}
							activeTab={activeTab}
							onTabChange={setActiveTab}
							variant='underline'
							className='w-full'
						/>
					</div>
				</ErrorBoundary>

				<Snackbar
					open={snackbar.open}
					onClose={() => setSnackbar({ ...snackbar, open: false })}
					message={snackbar.message}
					type={snackbar.type}
				/>
			</div>
		</ErrorBoundary>
	);
};

export default PLSummary;
